import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { appConstants } from '../../app-constants';
import { JwtDecoded } from '../../auth/models/jwt-decoded.model';
import { RefreshTokenLogin } from '../../auth/models/refresh-token-login.model';
import { NewPassword } from '../models/new-password.model';
import { ResetPassword } from '../models/reset-password.model';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	jwtHelperService: JwtHelperService;

	constructor(private route: ActivatedRoute) {
		this.jwtHelperService = new JwtHelperService();
	}

	/**
	 * Clear user data stored in the storage
	 * Redirect with reload to the auth
	 */
	logout(): void {
		this.removeAccessToken();
		this.removeRefreshToken();

		location.replace(`${location.origin}/auth`);
	}

	getAccessToken(): string | null {
		return localStorage.getItem(appConstants.LOCAL_STORAGE.ACCESS_TOKEN);
	}

	getRefreshToken(): string | null {
		return localStorage.getItem(appConstants.LOCAL_STORAGE.REFRESH_TOKEN);
	}

	setAccessToken(token?: string): void {
		if (!token) {
			return;
		}

		localStorage.setItem(appConstants.LOCAL_STORAGE.ACCESS_TOKEN, token);
	}

	setRefreshToken(token?: string): void {
		if (!token) {
			return;
		}

		localStorage.setItem(appConstants.LOCAL_STORAGE.REFRESH_TOKEN, token);
	}

	setTokens(accessToken: string, refreshToken: string): void {
		this.setAccessToken(accessToken);
		this.setRefreshToken(refreshToken);
	}

	removeAccessToken(): void {
		localStorage.removeItem(appConstants.LOCAL_STORAGE.ACCESS_TOKEN);
	}

	removeRefreshToken(): void {
		localStorage.removeItem(appConstants.LOCAL_STORAGE.REFRESH_TOKEN);
	}

	isAccessTokenExpired(): boolean {
		const token = this.getAccessToken();

		if (!token) {
			return true;
		}

		return this.jwtHelperService.isTokenExpired(token);
	}

	getOAuthToken(): string {
		return btoa(appConstants.CLIENT_ID_SECRET);
	}

	getRefreshTokenData(): RefreshTokenLogin {
		const refreshToken = this.getRefreshToken();
		const accessToken = this.getAccessToken();

		return {
			accessToken: accessToken || '',
			refreshToken: refreshToken || '',
		};
	}

	decodeAccessToken(): JwtDecoded | null {
		const token = this.getAccessToken();

		if (!token) {
			return null;
		}

		return this.jwtHelperService.decodeToken(token);
	}

	buildNewPasswordModel(passwordFormData: { oldPassword: string; newPassword: string }): NewPassword {
		return {
			oldPassword: passwordFormData.oldPassword,
			newPassword: passwordFormData.newPassword,
			resetHash: (this.route.snapshot.queryParamMap.get(appConstants.QUERY_PARAMS.RESET_HASH) || '').split(' ').join('+'),
		};
	}

	buildResetPasswordModel(passwordFormData: { newPassword: string; confirmPassword: string }): ResetPassword {
		return {
			newPassword: passwordFormData.newPassword,
			confirmPassword: passwordFormData.confirmPassword,
			resetHash: (this.route.snapshot.queryParamMap.get(appConstants.QUERY_PARAMS.RESET_HASH) || '').split(' ').join('+'),
		};
	}
}
